import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material'
import { useDraggable } from '@dnd-kit/core'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DashboardCell, DashboardRow, MultiTableAnalysisWidgetData } from '../../../types/gotypes'
import { calculateCellWidths } from '../../../utils/dashboard'
import {
  MIN_CELL_WIDTH_PCT,
  STACK_BREAKPOINT,
  redistributeWidths,
  resolveRowHeight,
} from '../../../utils/dashboardBuilderLayout'
import LayoutDropZone from '../layout/LayoutDropZone'
import CellBoundaryHandle from '../layout/CellBoundaryHandle'
import { LayoutDragData } from '../layout/layoutDnd'
import { RichTextRenderer } from '../widgets/RichTextWidget'
import DashboardLayoutComponent from '../DashboardLayoutComponent'
import { MultiTableAnalysisWidgetRenderer } from '../widgets/MultiTableAnalysisWidget'
import { ImageWidgetRenderer } from '../widgets/ImageWidget'
import { VideoWidgetRenderer } from '../widgets/VideoWidget'
import { TableGraphWidgetRenderer } from '../widgets/TableGraphWidget'
import { AgentArtifactWidgetRenderer } from '../widgets/AgentArtifactWidget'
import { TitleHeroWidgetRenderer } from '../widgets/TitleHeroWidget'
import { KPICardWidgetRenderer } from '../widgets/KPICardWidget'
import { CalloutWidgetRenderer } from '../widgets/CalloutWidget'
import { TOCWidgetRenderer } from '../widgets/TOCWidget'
import { summarisePillar } from '../widgets/ReportPillarWidget'
import { cardGlobalFilter } from '../../../utils/dashboardFilters'

type DeleteTarget = { type: 'row' } | { type: 'cell'; cellIndex: number } | null

/** What a `layout` cell (a column container, e.g. an exec-report pillar) holds,
 *  so Delete can name it before destroying it (#1075). Counts every widget in
 *  every inner row and takes the first `title_hero` heading as the column's
 *  name. */
export function summariseLayoutCell(cell: DashboardCell): { widgetCount: number; heading?: string } {
  let widgetCount = 0
  let heading: string | undefined
  for (const innerRow of cell.layout_data?.rows || []) {
    for (const innerCell of innerRow.cells) {
      widgetCount += 1
      if (!heading && innerCell.widget_type === 'title_hero' && innerCell.title_hero_data?.heading) {
        heading = innerCell.title_hero_data.heading
      }
    }
  }
  return { widgetCount, heading }
}

/**
 * The grab handle on a widget's control bar. Only the handle is draggable —
 * the widget itself stays fully interactive, so dragging can coexist with a
 * live chart, a scrolling table or an editable text box underneath.
 *
 * Declared at module level so it keeps one component identity across renders
 * (declaring it inside the row's render body would remount it every time,
 * cancelling any drag in progress).
 */
export const WidgetDragHandle: React.FC<{ rowIndex: number; cellIndex: number }> = ({ rowIndex, cellIndex }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `editor-cell-${rowIndex}-${cellIndex}`,
    data: { source: 'canvas', ref: { rowIndex, cellIndex } },
  })
  // #1129: MUI's uncontrolled tooltip never sees mouseleave once the drag
  // captures the pointer, so it sat open over the widget for the whole drag.
  // Controlled instead: open only while hovered AND not dragging, and hover is
  // cleared on pointer-down so the tooltip vanishes the moment the handle is
  // grabbed and stays away until the pointer re-enters after the drop.
  const [hovered, setHovered] = useState(false)
  // The pointer leaves the handle immediately during a drag, so `grabbing` on
  // the handle alone would flick back to an arrow. Hold it on the body for the
  // duration; the cleanup runs on drop AND on Escape-cancel (isDragging flips
  // false either way), so the cursor always restores.
  useEffect(() => {
    if (!isDragging) return
    const previous = document.body.style.cursor
    document.body.style.cursor = 'grabbing'
    return () => {
      document.body.style.cursor = previous
    }
  }, [isDragging])
  return (
    <Tooltip title="Drag to move this widget" open={hovered && !isDragging}>
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
          setHovered(false)
          listeners?.onPointerDown?.(e)
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label="Move widget"
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          px: 0.25,
          color: 'text.disabled',
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  )
}

interface DashboardEditorRowProps {
  row: DashboardRow
  rowIndex: number
  totalRows: number
  customer: string
  job: string
  dashboardId?: string
  /** #1219 gate 2 — see DashboardLayoutComponent.tsx's doc. The editor's own
   *  `canOverwrite`; #1262 already redirects a non-overwriter out of the
   *  editor, so this is belt-and-braces here rather than a live gap. */
  canRegenerate?: boolean
  globalFilter?: string
  jobFilters?: Record<string, string>
  colorMap?: Record<string, string>
  /** Theme id → flattened colour map (#1193) — resolves chart-level brand_palette_key. */
  themeColorMaps?: Record<string, Record<string, string>>
  onDeleteRow: () => void
  onEditCell: (cellIndex: number) => void
  onDeleteCell: (cellIndex: number) => void
  /** "Remove reference" on a broken table widget (#969 Part E). */
  onRemoveTableReference?: (cellIndex: number) => void
  onMoveRowUp: () => void
  onMoveRowDown: () => void
  /** Boundary drag (#1049) — moves width from cell `boundaryIndex` to the next. */
  onResizeBoundary: (boundaryIndex: number, deltaPct: number) => void
  onDistributeEvenly: () => void
  onInsertInnerRow?: (cellIndex: number, afterInnerRowIndex: number) => void
  onEditInnerCell?: (cellIndex: number, innerRowIndex: number, innerCellIndex: number) => void
  onDeleteInnerRow?: (cellIndex: number, innerRowIndex: number) => void
  onMoveInnerRow?: (cellIndex: number, innerRowIndex: number, direction: 'up' | 'down') => void
  onUpdateInnerCellData?: (cellIndex: number, innerRowIndex: number, innerCellIndex: number, updatedCell: DashboardCell) => void
  /** Duplicate — the whole of what "copy" means now (#967 resolved). The copy
   *  lands in the next slot and the layout adapts; there is nothing to aim and
   *  so nothing that can be refused. */
  onDuplicateCell?: (cellIndex: number) => void
  onDuplicateInnerCell?: (cellIndex: number, innerRowIndex: number, innerCellIndex: number) => void
  onDuplicateRow?: () => void
  /** A duplicate is in flight (forking multi-tables). Every duplicate control
   *  disables on it: a second click would fork a second time, orphan the first
   *  copy's multi-tables, and commit from a stale layout. */
  duplicateBusy?: boolean
  /** The in-flight drag, so the row can suppress the two no-op cell-gap
   *  targets adjacent to a widget being reordered within this row (#1111). */
  activeDrag?: LayoutDragData | null
}

const DashboardEditorRow: React.FC<DashboardEditorRowProps> = ({
  row,
  rowIndex,
  totalRows,
  customer,
  job,
  dashboardId,
  canRegenerate,
  globalFilter,
  jobFilters,
  colorMap,
  themeColorMaps,
  onDeleteRow,
  onEditCell,
  onDeleteCell,
  onRemoveTableReference,
  onMoveRowUp,
  onMoveRowDown,
  onResizeBoundary,
  onDistributeEvenly,
  onInsertInnerRow,
  onEditInnerCell,
  onDeleteInnerRow,
  onMoveInnerRow,
  onUpdateInnerCellData,
  onDuplicateCell,
  onDuplicateInnerCell,
  onDuplicateRow,
  duplicateBusy,
  activeDrag,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<DeleteTarget>(null)
  const cellsRef = useRef<HTMLDivElement | null>(null)
  // Live geometry while a boundary handle is held. Null between drags, so the
  // committed layout stays the single source of truth rather than being
  // mirrored into state (which is what would let the two disagree).
  const [previewWidths, setPreviewWidths] = useState<number[] | null>(null)

  const cellWidths = previewWidths || calculateCellWidths(row.cells)
  // Any height saved before the drag was removed (#1177) still applies;
  // resolveRowHeight keeps it at or above the row's floor.
  const rowHeight = resolveRowHeight(row)
  // #1111/#1116: every boundary is a drop target — no cell cap. The only
  // zones NOT rendered are the two no-op targets either side of a widget
  // being dragged within this same row (dropping there changes nothing).
  const suppressCellGap = (index: number): boolean =>
    activeDrag?.source === 'canvas' &&
    activeDrag.ref.rowIndex === rowIndex &&
    (index === activeDrag.ref.cellIndex || index === activeDrag.ref.cellIndex + 1)

  // Non-null only while the pending delete targets a `layout` cell — drives the
  // richer confirmation copy below (#1075). Derived, never stored.
  const deletingCell = confirmDelete?.type === 'cell' ? row.cells[confirmDelete.cellIndex] : undefined
  // A report pillar (#1080) is a whole column of cards too, so its Delete keeps
  // the same #1075 confirmation — named contents before anything is destroyed.
  const deletingLayoutSummary = deletingCell?.widget_type === 'layout'
    ? summariseLayoutCell(deletingCell)
    : deletingCell?.widget_type === 'report_pillar'
      ? summarisePillar(deletingCell.report_pillar_data)
      : undefined

  const handleConfirmDelete = () => {
    if (!confirmDelete) return
    if (confirmDelete.type === 'row') {
      onDeleteRow()
    } else {
      onDeleteCell(confirmDelete.cellIndex)
    }
    setConfirmDelete(null)
  }

  /** An empty widget invites its own editor — the click path that replaced
   *  "Insert Cell" now that structure comes from where a widget is dropped
   *  (#1015). A `layout` cell has no editor of its own (#1075), so its
   *  placeholder stays inert. */
  const emptyPlaceholder = (cellIndex: number, clickable: boolean) => (
    <Box
      onClick={clickable ? () => onEditCell(cellIndex) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 60,
        opacity: 0.4,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { opacity: 0.7 } : undefined,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Click to add content
      </Typography>
    </Box>
  )

  const isCellEmpty = (cell: typeof row.cells[0]): boolean => {
    switch (cell.widget_type) {
      case 'text':
        return !cell.text_data?.content
      case 'layout':
        return !cell.layout_data
      case 'report_pillar':
        return !cell.report_pillar_data?.entries?.length
      case 'multi_table_analysis':
        return !cell.multi_table_analysis_data?.multi_table_id
      case 'image':
        return !cell.image_data?.file_path
      case 'video':
        return !cell.video_data?.embed_url
      case 'table_graph':
        return !cell.table_graph_data?.table_path && !cell.table_graph_data?.spec
      case 'agent_artifacts':
        return !cell.agent_artifact_data
        return !cell.table_graph_data?.table_path
      case 'title_hero':
        return !cell.title_hero_data?.heading
      case 'toc':
        return !cell.toc_data
      case 'kpi_card':
        return !cell.kpi_card_data?.value
      case 'callout':
        return !cell.callout_data?.title
      case 'spacer':
        // #1187: a spacer has no data to configure — its "content" is the
        // empty space itself, so it is never "empty" and never invites the
        // Click-to-add placeholder.
        return false
      default:
        return true
    }
  }

  /** Edit-mode rendering of a spacer (#1187): invisible in view mode, so here
   *  it gets a subtle dashed outline and label — enough to be found, selected,
   *  resized and deleted, without pretending to be content. */
  const spacerEditPlaceholder = (
    <Box
      sx={{
        flex: 1,
        minHeight: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        opacity: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
        Spacer
      </Typography>
    </Box>
  )

  /** Render content for inner cells within a nested layout */
  const renderInnerCellContent = (cell: typeof row.cells[0], outerCellIndex: number, innerRowIndex: number, innerCellIndex: number) => {
    switch (cell.widget_type) {
      case 'text':
        return cell.text_data?.content ? <RichTextRenderer textData={cell.text_data} /> : null
      case 'multi_table_analysis':
        if (cell.multi_table_analysis_data?.multi_table_id && customer) {
          const widgetJob = cell.multi_table_analysis_data.job || job
          return (
            <MultiTableAnalysisWidgetRenderer
              data={cell.multi_table_analysis_data}
              customer={customer}
              job={widgetJob}
              dashboardId={dashboardId}
              canRegenerate={canRegenerate}
              // #1810: an editor Run snapshots the page's filters, so the
              // card must see them too or every card just run is "stale".
              globalFilter={cardGlobalFilter(widgetJob, job, jobFilters, globalFilter)}
              jobFilters={jobFilters}
              onUpdateData={onUpdateInnerCellData ? (updatedData: MultiTableAnalysisWidgetData) => {
                onUpdateInnerCellData(outerCellIndex, innerRowIndex, innerCellIndex, { ...cell, multi_table_analysis_data: updatedData })
              } : undefined}
              onOpenSettings={onEditInnerCell ? () => onEditInnerCell(outerCellIndex, innerRowIndex, innerCellIndex) : undefined}
            />
          )
        }
        return <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>Analysis not configured</Typography>
      case 'title_hero':
        if (cell.title_hero_data) return <TitleHeroWidgetRenderer data={cell.title_hero_data} />
        return null
      case 'image':
        if (cell.image_data && customer) return <ImageWidgetRenderer data={cell.image_data} customer={customer} />
        return null
      case 'table_graph':
        if (cell.table_graph_data && customer) {
          const tgJob = cell.table_graph_data.job || job
          return (
            <TableGraphWidgetRenderer
              data={cell.table_graph_data}
              customer={customer}
              job={tgJob}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
              onSelectAlternative={onEditInnerCell ? () => onEditInnerCell(outerCellIndex, innerRowIndex, innerCellIndex) : undefined}
              onRemoveReference={onUpdateInnerCellData ? () => onUpdateInnerCellData(outerCellIndex, innerRowIndex, innerCellIndex, {
                ...cell,
                table_graph_data: { job: cell.table_graph_data!.job, table_path: '', view_mode: cell.table_graph_data!.view_mode || 'table' },
              }) : undefined}
            />
          )
        }
        return null
      case 'kpi_card':
        if (cell.kpi_card_data) return <KPICardWidgetRenderer data={cell.kpi_card_data} />
        return null
      case 'callout':
        if (cell.callout_data) return <CalloutWidgetRenderer data={cell.callout_data} />
        return null
      case 'spacer':
        return spacerEditPlaceholder
      default:
        return <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>{cell.widget_type || 'Empty'}</Typography>
    }
  }

  const renderCellContent = (cell: typeof row.cells[0], cellIndex: number) => {
    if (isCellEmpty(cell)) return emptyPlaceholder(cellIndex, cell.widget_type !== 'layout')

    switch (cell.widget_type) {
      case 'text':
        return <RichTextRenderer textData={cell.text_data} />
      case 'layout':
        if (cell.layout_data) {
          const canEditInner = Boolean(onInsertInnerRow)
          if (canEditInner) {
            // Editable nested layout — show inner rows with insert/edit/delete controls
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {cell.layout_data.rows.map((innerRow, innerRowIndex) => (
                  <React.Fragment key={innerRowIndex}>
                    {/* Insert inner row divider — before first row */}
                    {innerRowIndex === 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mx: 1,
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          '&:hover': { opacity: 1 },
                          height: 16,
                        }}
                      >
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'info.main', opacity: 0.4 }} />
                        <Tooltip title="Insert widget above">
                          <Box
                            onClick={() => onInsertInnerRow!(cellIndex, innerRowIndex - 1)}
                            sx={{
                              width: 18, height: 18, borderRadius: '50%',
                              backgroundColor: 'info.main', color: 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', mx: 0.5,
                            }}
                          >
                            <AddIcon sx={{ fontSize: 12 }} />
                          </Box>
                        </Tooltip>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'info.main', opacity: 0.4 }} />
                      </Box>
                    )}

                    {/* Inner row with hover controls */}
                    <Box
                      sx={{
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          '& .inner-row-controls': { opacity: 1 },
                        },
                        borderRadius: 0.5,
                        p: 0.5,
                      }}
                    >
                      {/* Inner row hover controls — top-left */}
                      <Box
                        className="inner-row-controls"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          display: 'flex',
                          gap: 0.25,
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          zIndex: 2,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          borderRadius: 0.5,
                          px: 0.25,
                        }}
                      >
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={innerRowIndex === 0}
                              onClick={() => onMoveInnerRow?.(cellIndex, innerRowIndex, 'up')}
                              aria-label={`Move item ${innerRowIndex + 1} up`}
                              sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={innerRowIndex === cell.layout_data!.rows.length - 1}
                              onClick={() => onMoveInnerRow?.(cellIndex, innerRowIndex, 'down')}
                              aria-label={`Move item ${innerRowIndex + 1} down`}
                              sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
                            >
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {onDuplicateInnerCell && innerRow.cells[0] && (
                          <Tooltip title="Duplicate widget">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Duplicate widget"
                                disabled={duplicateBusy}
                                onClick={() => onDuplicateInnerCell(cellIndex, innerRowIndex, 0)}
                                sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onDeleteInnerRow && (
                          <Tooltip title="Delete section">
                            <IconButton
                              size="small"
                              onClick={() => onDeleteInnerRow(cellIndex, innerRowIndex)}
                              aria-label={`Delete item ${innerRowIndex + 1}`}
                              sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Render inner row cells using read-only renderers */}
                      {innerRow.cells.map((innerCell, innerCellIdx) => (
                        <Box key={innerCellIdx} sx={{ overflow: 'hidden' }}>
                          {renderInnerCellContent(innerCell, cellIndex, innerRowIndex, innerCellIdx)}
                        </Box>
                      ))}
                    </Box>

                    {/* Insert inner row divider — after each row */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mx: 1,
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        '&:hover': { opacity: 1 },
                        height: 16,
                      }}
                    >
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'info.main', opacity: 0.4 }} />
                      <Tooltip title="Insert widget below">
                        <Box
                          onClick={() => onInsertInnerRow!(cellIndex, innerRowIndex)}
                          sx={{
                            width: 18, height: 18, borderRadius: '50%',
                            backgroundColor: 'info.main', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', mx: 0.5,
                          }}
                        >
                          <AddIcon sx={{ fontSize: 12 }} />
                        </Box>
                      </Tooltip>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'info.main', opacity: 0.4 }} />
                    </Box>
                  </React.Fragment>
                ))}
                {/* Show add/paste when column is empty */}
                {cell.layout_data.rows.length === 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                    <Tooltip title="Add widget">
                      <IconButton
                        size="small"
                        onClick={() => onInsertInnerRow!(cellIndex, -1)}
                        aria-label="Add widget"
                        sx={{ '&:hover': { color: 'info.main' } }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )
          }
          // Fallback: read-only nested layout
          return (
            <DashboardLayoutComponent
              layout={cell.layout_data}
              customer={customer}
              job={job}
              canRegenerate={canRegenerate}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
            />
          )
        }
        return null
      case 'report_pillar': {
        // #1080. Entries render through the SAME inner-cell renderer a nested
        // layout's rows use, addressed as (entryIndex, 0) — so a card inside a
        // pillar keeps its cog, its live updates and its widget address.
        //
        // There are deliberately NO structural controls here: adding, removing
        // and renaming happen in the pillar's own editor, reached from the cell
        // chrome's cog. Reorder is withheld entirely in this build — entry
        // order is the report's narrative, pending #1080 Q2.
        const entries = cell.report_pillar_data?.entries || []
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {entries.map((entry, entryIndex) => (
              <Box key={entryIndex} sx={{ width: '100%' }}>
                {renderInnerCellContent(entry.cell, cellIndex, entryIndex, 0)}
              </Box>
            ))}
          </Box>
        )
      }
      case 'multi_table_analysis':
        if (cell.multi_table_analysis_data?.multi_table_id && customer) {
          const widgetJob = cell.multi_table_analysis_data.job || job
          return (
            <MultiTableAnalysisWidgetRenderer
              data={cell.multi_table_analysis_data}
              customer={customer}
              job={widgetJob}
              dashboardId={dashboardId}
              canRegenerate={canRegenerate}
              globalFilter={cardGlobalFilter(widgetJob, job, jobFilters, globalFilter)}
              jobFilters={jobFilters}
              onOpenSettings={() => onEditCell(cellIndex)}
            />
          )
        }
        return null
      case 'image':
        if (cell.image_data && customer) {
          return <ImageWidgetRenderer data={cell.image_data} customer={customer} />
        }
        return null
      case 'video':
        if (cell.video_data && customer) {
          return <VideoWidgetRenderer data={cell.video_data} customer={customer} />
        }
        return null
      case 'table_graph':
        if (cell.table_graph_data && customer) {
          const tgJob = cell.table_graph_data.job || job
          return (
            <TableGraphWidgetRenderer
              data={cell.table_graph_data}
              customer={customer}
              job={tgJob}
              globalFilter={globalFilter}
              jobFilters={jobFilters}
              colorMap={colorMap}
              themeColorMaps={themeColorMaps}
              onSelectAlternative={() => onEditCell(cellIndex)}
              onRemoveReference={onRemoveTableReference ? () => onRemoveTableReference(cellIndex) : undefined}
            />
          )
        }
        return null
      case 'agent_artifacts':
        if (cell.agent_artifact_data) {
          return <AgentArtifactWidgetRenderer data={cell.agent_artifact_data} />
        }
        return null
      case 'title_hero':
        if (cell.title_hero_data) {
          return <TitleHeroWidgetRenderer data={cell.title_hero_data} />
        }
        return null
      case 'toc':
        if (cell.toc_data) {
          return <TOCWidgetRenderer data={cell.toc_data} />
        }
        return null
      case 'kpi_card':
        if (cell.kpi_card_data) {
          return <KPICardWidgetRenderer data={cell.kpi_card_data} />
        }
        return null
      case 'callout':
        if (cell.callout_data) {
          return <CalloutWidgetRenderer data={cell.callout_data} />
        }
        return null
      case 'spacer':
        return spacerEditPlaceholder
      default:
        return null
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          position: 'relative',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          // A MINIMUM, not a fixed box: the row is as tall as its floor (or a
          // height saved before #1177) and its widgets fill it (fit-to-cell),
          // but content that needs more room still grows the row rather than
          // being clipped.
          minHeight: rowHeight,
          '&:hover': {
            '& .row-controls': { opacity: 1 },
            '& .cell-controls': { opacity: 1 },
            '& [role="separator"]': { opacity: 1 },
          },
        }}
      >
        {/* Row controls — left gutter, revealed on hover */}
        <Box
          className="row-controls"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            px: 0.5,
            py: 1,
            borderRight: '1px solid',
            borderColor: 'grey.200',
            backgroundColor: 'grey.50',
            minWidth: 28,
            opacity: 0,
            transition: 'opacity 0.15s',
          }}
        >
          <Tooltip title="Move row up" placement="right">
            <span>
              <IconButton
                size="small"
                onClick={onMoveRowUp}
                disabled={rowIndex === 0}
                aria-label={`Move row ${rowIndex + 1} up`}
                sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move row down" placement="right">
            <span>
              <IconButton
                size="small"
                onClick={onMoveRowDown}
                disabled={rowIndex === totalRows - 1}
                aria-label={`Move row ${rowIndex + 1} down`}
                sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
              >
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ width: 12, height: '1px', backgroundColor: 'grey.300', my: 0.25 }} />
          {onDuplicateRow && (
            <Tooltip title="Duplicate row" placement="right">
              <span>
                <IconButton
                  size="small"
                  aria-label="Duplicate row"
                  disabled={duplicateBusy}
                  onClick={onDuplicateRow}
                  sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Delete row" placement="right">
            <IconButton
              size="small"
              onClick={() => setConfirmDelete({ type: 'row' })}
              aria-label={`Delete row ${rowIndex + 1}`}
              sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Widgets. Structure comes from where a widget is dropped: the gaps
            between them are drop targets, not "insert cell" buttons (#1015).
            Below the stacking breakpoint the row becomes one column, in
            order — the only responsive rule in the model. */}
        <Box
          ref={cellsRef}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', [STACK_BREAKPOINT]: 'row' },
            flex: 1,
            minWidth: 0,
          }}
        >
          {!suppressCellGap(0) && (
            <LayoutDropZone
              id={`editor-cellgap-${rowIndex}-0`}
              orientation="column"
              target={{ kind: 'cell-gap', rowIndex, index: 0 }}
            />
          )}
          {row.cells.map((cell, cellIndex) => {
            // #1129: the cell being dragged reads as lifted — dimmed, dashed
            // outline, and deliberately NOT blue: blue is the drop indicator,
            // and it must mean exactly one thing during a drag.
            const isDragSource =
              activeDrag?.source === 'canvas' &&
              activeDrag.ref.rowIndex === rowIndex &&
              activeDrag.ref.cellIndex === cellIndex
            return (
            <React.Fragment key={cellIndex}>
              <Box
                data-testid={isDragSource ? 'drag-source-cell' : undefined}
                sx={{
                  width: { xs: '100%', [STACK_BREAKPOINT]: `${cellWidths[cellIndex]}%` },
                  minWidth: 0,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2,
                  pt: '36px', // room for the control bar
                  borderRight: cellIndex < row.cells.length - 1 ? '1px solid' : 'none',
                  borderColor: 'grey.200',
                  opacity: isDragSource ? 0.4 : 1,
                  outline: isDragSource ? '2px dashed' : 'none',
                  outlineColor: 'text.disabled',
                  outlineOffset: '-2px',
                  '&:hover': {
                    backgroundColor: 'grey.50',
                    '& .cell-controls': { opacity: 1 },
                  },
                }}
              >
              {/* Cell control bar — revealed on hover */}
              <Box
                className="cell-controls"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 0.5,
                  height: 28,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderBottom: '1px solid',
                  borderColor: 'grey.100',
                  opacity: 0,
                  transition: 'opacity 0.15s',
                  zIndex: 2,
                }}
              >
                {/* Left: the grab handle. Dragging a widget anywhere — another
                    slot in this row, another row, a seam between rows — has
                    replaced move-left/right and extract-to-new-row. */}
                <WidgetDragHandle rowIndex={rowIndex} cellIndex={cellIndex} />

                {/* Right: edit + duplicate + delete. Nothing else: rows and
                    columns are drag mechanics now, never button labels (#967). */}
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {/* #1075: a `layout` cell is a column container, not a widget —
                      it has no editor, so the pencil could only ever replace the
                      whole column (heading + every card inside) with one empty
                      widget. Its widgets are edited by their own inner controls. */}
                  {cell.widget_type !== 'layout' && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEditCell(cellIndex)}
                        aria-label={`Edit widget ${cellIndex + 1}`}
                        sx={{ p: 0.25, '&:hover': { color: 'info.main' } }}
                      >
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDuplicateCell && (() => {
                    const duplicateLabel = cell.widget_type === 'layout' ? 'Duplicate column'
                      : cell.widget_type === 'report_pillar' ? 'Duplicate pillar' : 'Duplicate widget'
                    return (
                    <Tooltip title={duplicateLabel}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={duplicateLabel}
                          disabled={duplicateBusy}
                          onClick={() => onDuplicateCell(cellIndex)}
                          sx={{ p: 0.25, '&:not(:disabled):hover': { color: 'info.main' } }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    )
                  })()}
                  <Tooltip title="Delete widget">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete({ type: 'cell', cellIndex })}
                      aria-label={`Delete widget ${cellIndex + 1}`}
                      sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Widget content. `flex: 1` is fit-to-cell: the widget fills the
                  height the row was dragged to, rather than sitting at its own
                  natural height with dead space under it. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 40, overflow: 'hidden' }}>
                {renderCellContent(cell, cellIndex)}
              </Box>

              {/* Boundary drag (#1049): takes width from this widget and gives
                  it to the next, and nothing else in the row moves. */}
              {cellIndex < row.cells.length - 1 && (
                <CellBoundaryHandle
                  getRowWidth={() => cellsRef.current?.clientWidth || 0}
                  onPreview={(deltaPct) => setPreviewWidths(
                    redistributeWidths(calculateCellWidths(row.cells), cellIndex, deltaPct, MIN_CELL_WIDTH_PCT)
                  )}
                  onCommit={(deltaPct) => {
                    setPreviewWidths(null)
                    onResizeBoundary(cellIndex, deltaPct)
                  }}
                  onDistributeEvenly={() => {
                    setPreviewWidths(null)
                    onDistributeEvenly()
                  }}
                />
              )}
            </Box>
            {!suppressCellGap(cellIndex + 1) && (
              <LayoutDropZone
                id={`editor-cellgap-${rowIndex}-${cellIndex + 1}`}
                orientation="column"
                target={{ kind: 'cell-gap', rowIndex, index: cellIndex + 1 }}
              />
            )}
            </React.Fragment>
            )
          })}
        </Box>
      </Box>

      {/* Confirm delete dialog. A `layout` cell — and, since #1080, a
          report_pillar — is a whole column, so its confirmation names what is
          inside before it goes (#1075); ordinary cells keep the plain
          title-only prompt. */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {confirmDelete?.type === 'row'
            ? 'Delete row?'
            : deletingCell?.widget_type === 'report_pillar'
              ? 'Delete pillar?'
              : deletingLayoutSummary
                ? 'Delete column?'
                : 'Delete widget?'}
        </DialogTitle>
        {deletingLayoutSummary && (
          <DialogContent sx={{ pb: 0 }}>
            <DialogContentText variant="body2">
              {(() => {
                const isPillar = deletingCell?.widget_type === 'report_pillar'
                const noun = isPillar ? 'card' : 'widget'
                const container = isPillar ? 'pillar' : 'column'
                const count = deletingLayoutSummary.widgetCount
                const plural = count === 1 ? noun : `${noun}s`
                return deletingLayoutSummary.heading
                  ? `“${deletingLayoutSummary.heading}” contains ${count} ${plural}. Deleting the ${container} deletes all of them. This cannot be undone.`
                  : `This ${container} contains ${count} ${plural}. Deleting the ${container} deletes all of them. This cannot be undone.`
              })()}
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} color="inherit" size="small">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DashboardEditorRow
